import { useEffect, useRef } from 'react'
import { useUser } from '@clerk/clerk-react'
import { Navigate } from 'react-router-dom'
import { useApi } from '@/hooks/use-api'
import { attributePendingReferral } from '@/features/affiliate/capture-referral'
import { attributePendingSignupSource } from '@/lib/capture-attribution'

/**
 * Auth-only gate: are we signed in? Nothing more.
 *
 * Onboarding + payment ordering moved out of here into <AccessGuard> on
 * 2026-06-12. The old admin-email allowlist + "Early Access Waitlist" wall
 * that used to live here are gone — the app is open to anyone who signs up,
 * completes onboarding, and subscribes (staff/comp accounts are entitled
 * server-side, so they skip the payment wall). Keeping this component focused
 * on "is there a session?" means there's exactly one place that decides the
 * first-run journey (AccessGuard), instead of two guards fighting.
 */
export function ProtectedRoute({ children }: { readonly children: React.ReactNode }) {
  const { isLoaded, isSignedIn, user } = useUser()
  const apiFetch = useApi()

  // Attribute a stashed ?ref=CODE once the user is authenticated. Runs at
  // most once per mounted session; the backend is idempotent so duplicate
  // calls would be no-ops, but the ref-once guard saves a network round trip.
  const attributionAttempted = useRef(false)
  useEffect(() => {
    if (!isLoaded || !isSignedIn || !user) return
    if (attributionAttempted.current) return
    attributionAttempted.current = true
    void attributePendingReferral(apiFetch).catch(() => {
      // Silent — capture-referral.ts already swallows errors and clears the
      // stash, so there's nothing to retry or report.
    })
    // Same one-shot semantics for the UTM/source snapshot (first touch wins
    // server-side, stash cleared after any attempt).
    void attributePendingSignupSource(apiFetch).catch(() => {})
  }, [isLoaded, isSignedIn, user, apiFetch])

  if (!isLoaded) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    )
  }

  if (!isSignedIn || !user) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}
